import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, HelpCircle, FileText, ExternalLink, Download } from 'lucide-react';

const GUIDE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/help/user-guide`;

export default function HelpPage() {
  const navigate = useNavigate();

  const openGuide = () => window.open(GUIDE_URL, '_blank', 'noopener');
  const downloadGuide = () => window.open(`${GUIDE_URL}?download=true`, '_blank', 'noopener');

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="help-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-blue-600" />
              <span className="font-semibold">Help &amp; Documentation</span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="help-home-btn">
            Back to App
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="help-page">
        <div className="text-center space-y-2 mb-8">
          <h1 className="text-3xl font-bold">BHPL-ERP/SM User Guide</h1>
          <p className="text-muted-foreground">
            The complete picturised operations manual — Inventory, CTS, Production, Dispatch &amp; Procurement.
          </p>
        </div>

        <Card className="border-blue-200 shadow-sm">
          <CardContent className="p-6 flex flex-col sm:flex-row items-center gap-5">
            <div className="w-16 h-16 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-lg font-semibold">Picturised Operations Manual</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Step-by-step guide with screenshots for every core workflow. Opens as a Word document.
              </p>
              <div className="flex flex-wrap gap-2 mt-4 justify-center sm:justify-start">
                <Button onClick={openGuide} className="bg-blue-600 hover:bg-blue-700" data-testid="open-user-guide-btn">
                  <ExternalLink className="w-4 h-4 mr-1.5" /> Open User Guide
                </Button>
                <Button variant="outline" onClick={downloadGuide} data-testid="download-user-guide-btn">
                  <Download className="w-4 h-4 mr-1.5" /> Download
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm text-muted-foreground list-disc list-inside">
          <li>CTS Material Entry &amp; Reconciliation</li>
          <li>Daily Production Plan</li>
          <li>Work Order &amp; Work Order Receipt</li>
          <li>Quick Dispatch (challan + e-mail)</li>
          <li>MRP Planning</li>
          <li>Purchase Indent, RFQ &amp; PO</li>
        </ul>
      </div>
    </div>
  );
}
